import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calendar,
  Star,
  Settings,
  User,
  Search,
  LogOut,
  CheckCircle,
  X,
  Heart,
  Bell,
  HelpCircle,
} from 'lucide-react';

const ClientSidebar = ({ isMobileOpen, onClose }) => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  const navigationGroups = [
    {
      label: 'Account',
      items: [
        { name: 'My account', href: '/dashboard', icon: User },
        { name: 'Settings', href: '/settings', icon: Settings },
      ],
    },
    {
      label: 'Bookings',
      items: [
        { name: 'My Bookings', href: '/bookings', icon: Calendar },
        { name: 'Favorites', href: '/favorites', icon: Heart },
        { name: 'My Reviews', href: '/reviews', icon: Star },
      ],
    },
    {
      label: 'Discover',
      items: [{ name: 'Browse Vendors', href: '/browse-vendors', icon: Search }],
    },
    {
      label: 'Support',
      items: [
        { name: 'Notifications', href: '/notifications', icon: Bell },
        { name: 'Help & Support', href: '/help', icon: HelpCircle },
      ],
    },
  ];

  return (
    <>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 dashboard-surface bg-black/50 z-[11000]"
          onClick={onClose}
        />
      )}

      <div
        className={`
        w-full lg:w-64 flex-shrink-0
        ${
          isMobileOpen
            ? 'fixed inset-y-0 left-0 z-50 lg:relative lg:z-auto'
            : 'hidden lg:block'
        }
        transition-transform duration-300 ease-in-out
      `}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-700 h-full lg:h-auto lg:sticky lg:top-24 flex flex-col shadow-sm overflow-hidden">
          {isMobileOpen && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 lg:hidden">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Menu</h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 !rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 dark:bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-white dark:text-gray-900 font-semibold text-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'C'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {user?.name || 'Client'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || ''}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <CheckCircle className="w-3 h-3" />
                  Active
                </span>
              </div>
            </div>
          </div>

          <nav className="p-3.5 space-y-5 flex-1 overflow-y-auto">
            {navigationGroups.map((group) => (
              <div key={group.label} className="space-y-1.5">
                <p className="px-2.5 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  {group.label}
                </p>
                {group.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      if (isMobileOpen && onClose) onClose();
                    }}
                    className={({ isActive }) =>
                      `flex items-center px-2.5 py-3 text-sm font-medium !rounded-xl transition-colors ${
                        isActive
                          ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="p-3 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center w-full px-2.5 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 !rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientSidebar;
